import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../lib/api";
import RecordedRefundPanel from "../../commerce/RecordedRefundPanel";
import DuplicateCollectionRefund from "./DuplicateCollectionRefund";
import {
  CAPTURED_PAYMENT_STATUSES,
  isRefundablePaymentStatus,
  paymentStatusColor,
  paymentStatusLabel,
} from "../../../lib/paymentStatusDisplay";
import { toast } from "sonner";
import { Loader2, X, Info } from "lucide-react";
import OrderShippingPanel from "./OrderShippingPanel";
import OrderLocalDeliveryPanel from "./OrderLocalDeliveryPanel";
import UberQuoteStaffCard from "./UberQuoteStaffCard";
import OwnDeliveryOperationalPanel from "./OwnDeliveryOperationalPanel";
import PickupOperationalPanel from "./PickupOperationalPanel";
import TenantReviewModal from "./TenantReviewModal";
import OrderDiscountPanel from "./OrderDiscountPanel";
import StripeTerminalRecoveryPanel from "./StripeTerminalRecoveryPanel";
import {
  orderPaymentBreakdown,
  externalPaidLabel,
} from "../../../lib/orderPaymentBreakdown";
import { fulfillmentPromise, modeLabel } from "../../../lib/retail/fulfillmentPresentation";
import TakeoutBadge from "../TakeoutBadge";
import { lineTaxableMap, taxMarkedName } from "../../../lib/retail/lineTax";

// Sprint A (Feb 2026) — Retail Orders Admin drawer.
// Opened from the clickable order code on /dashboard/retail-orders. Shows
// the full order breakdown, payment history, refund history, order
// status history, and an operator-only "Change payment method" control
// for post-hoc record correction (cash / credit / check only).
//
// Design intent: this is a read-mostly detail view — the two actions
// available are "Change payment method" (record correction, guarded by
// the backend) and "Issue refund" (delegates to the existing
// RefundOrderModal via the onRequestRefund callback). Everything else
// is display-only so the operator can't accidentally corrupt the
// canonical order.

// Aug 2026 sandbox-validation-blocker correction — this map previously
// only covered the terminal payment states, so `PAYMENT_STATUS_LABEL[ps]
// || "Pending"` silently showed "Pending" for every Local Delivery
// intermediate status below, even though the Stripe payment underneath
// each of them is already captured (see `amount_captured` rendered right
// alongside it) — the exact contradiction this correction closes. Every
// value here mirrors `routes/payments.py`'s own `_TRUTHFUL_INTERMEDIATE_
// ORDER_STATUSES` set (the authoritative list of "payment captured, order
// not yet fully resolved" states), never a fourth independently-derived
// list. This is a display-only correction — it does not change what value
// is stored, only how it is labeled for the tenant.
// Sep 2026 (independent review of PR #185) — the captured-status set,
// the label map and the colour map moved to
// `lib/paymentStatusDisplay.js`, shared with `RetailOrdersAdmin.jsx`.
// Two findings came out of three private copies drifting apart:
//
//   * PR #185 writes a NEW order status, `cancelled`, from the kiosk
//     cancel, the device cancel and the TTL abandon. Neither label map
//     knew it, and both fall back to "Pending" — so the abandoned
//     attempt still read *Pending* on the Retail Orders screen, beside
//     a drawer row that said "Payment cancelled".
//   * this file's own captured set already listed the Local-Delivery
//     and tenant-review states where money IS collected, while the
//     refund-panel gate below listed only `paid`/`partial_refund`. The
//     drawer therefore said "Amount captured" and "no payment has been
//     confirmed as collected" about the same order, and hid the refund
//     control on a genuinely captured Local Delivery order.


// Kitchen/Delivery/Payment status-axis separation correction (Aug
// 2026) — the drawer previously showed Payment status but never the
// restaurant's own Kitchen status, so a reader had to infer it from
// context. Values/labels mirror `RetailOrdersAdmin.jsx`'s own
// STATUS_LABEL/COLOR (same local-copy convention already used for
// PAYMENT_STATUS_LABEL above).
const KITCHEN_STATUS_LABEL = {
  placed: "Placed",
  accepted: "Accepted",
  ready: "Ready",
  completed: "Completed",
  cancelled: "Order Cancelled",
};
const KITCHEN_STATUS_COLOR = {
  placed: "bg-slate-100 text-slate-700",
  accepted: "bg-blue-100 text-blue-700",
  ready: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};
// Same vocabulary as `OrderLocalDeliveryPanel.jsx`'s own
// DELIVERY_STATUS_LABEL/COLOR — kept as a separate local copy here so
// this file's own display concerns never depend on that panel's
// internals (it's a sibling component, not a shared library).
const DELIVERY_STATUS_LABEL = {
  not_applicable: "Not applicable",
  quote_pending: "Getting delivery quote…",
  quoted: "Quote received",
  quote_expired: "Quote expired",
  dispatch_pending: "Requesting courier…",
  courier_requested: "Courier requested",
  courier_assigned: "Courier assigned",
  en_route_to_pickup: "Courier en route to pickup",
  at_pickup: "Courier at restaurant",
  picked_up: "Picked up by courier",
  en_route_to_dropoff: "Out for delivery",
  delivered: "Delivered",
  cancellation_pending: "Cancellation pending",
  canceled: "Delivery canceled",
  delivery_failed: "Delivery request failed",
  returned: "Returned",
  reconciliation_required: "Needs attention",
};
const DELIVERY_STATUS_COLOR = {
  delivered: "bg-emerald-100 text-emerald-700",
  courier_requested: "bg-sky-100 text-sky-700",
  courier_assigned: "bg-sky-100 text-sky-700",
  en_route_to_pickup: "bg-sky-100 text-sky-700",
  at_pickup: "bg-sky-100 text-sky-700",
  picked_up: "bg-sky-100 text-sky-700",
  en_route_to_dropoff: "bg-sky-100 text-sky-700",
  dispatch_pending: "bg-slate-100 text-slate-600",
  delivery_failed: "bg-rose-100 text-rose-700",
  canceled: "bg-rose-100 text-rose-700",
  reconciliation_required: "bg-amber-100 text-amber-800",
  quote_expired: "bg-amber-100 text-amber-800",
};

const OPERATOR_PAYMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "credit", label: "Credit (record only)" },
  { value: "check", label: "Check (record only)" },
];

function formatMoney(v, currency) {
  if (v == null) return "";
  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "").toUpperCase()] || "";
  return sym ? `${sym}${Number(v).toFixed(2)}` : `${Number(v).toFixed(2)} ${currency || ""}`.trim();
}
function centsToDollars(c) { return Number(((c || 0) / 100).toFixed(2)); }
function redactStripeId(id) {
  // Keep the first 6 (prefix like `pi_test`, `cs_live`) and the last 4.
  if (!id || typeof id !== "string" || id.length <= 12) return id || "";
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

// Which rail took (or attempted) a payment. Read from the ledger row
// rather than inferred from the order, so a Terminal attempt is never
// described as Hosted Checkout and vice versa — the `cs_live_…`-vs-`pi_…`
// confusion on RUD-ORD-000004 is exactly what this exists to prevent.
// Shared by the payment-channel row and the duplicate-collection list.
function paymentRailLabel(row) {
  const provider = String(row?.provider || "").toLowerCase();
  const method = String(row?.method || "").toLowerCase();
  if (provider === "stripe_terminal" || method === "stripe_terminal") return "Card reader (Stripe Terminal)";
  if (provider === "clover_terminal" || method === "clover_terminal") return "Card reader (Clover)";
  if (provider === "cash" || method === "cash") return "Cash";
  if (provider === "stripe") return "Hosted Checkout (online card)";
  return provider ? provider.replace(/_/g, " ") : null;
}

export default function OrderDetailsDrawer({ order, onClose, onChanged, onRequestRefund }) {
  const [loading, setLoading] = useState(true);
  const [orderDoc, setOrderDoc] = useState(order);
  const [payment, setPayment] = useState(null);
  // Sep 2026 (second independent review of PR #185) — genuinely collected
  // payments that did NOT settle this order. Real money the customer is
  // owed back; shown separately and never folded into the order's own
  // figures. See `payments/settlement_claim.py`.
  const [duplicateCollections, setDuplicateCollections] = useState([]);
  const [error, setError] = useState(null);
  // Payment method change form
  const [pmChanging, setPmChanging] = useState(false);
  const [newPm, setNewPm] = useState("");
  const [pmReason, setPmReason] = useState("");
  // Catalog-level Stripe authorization/tenant-review (Aug 2026).
  const [showTenantReview, setShowTenantReview] = useState(false);
  // Financial-safety audit (Aug 2026) — the tenant-scoped recovery
  // action for a payment stuck `reconciliation_required`.
  const [reconciling, setReconciling] = useState(false);
  // Post-capture fulfillment-recovery correction (Aug 2026) — the
  // explicit tenant action for an order stuck after Stripe capture but
  // before Kitchen Accepted/Uber dispatch/KDS admission completed.
  const [resumingFulfillment, setResumingFulfillment] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/retail/orders/${order.id}/payment`);
      setOrderDoc(data?.order || order);
      setPayment(data?.payment || null);
      setDuplicateCollections(
        Array.isArray(data?.duplicate_collections) ? data.duplicate_collections : [],
      );
    } catch (e) {
      setError(e?.response?.status === 404 ? "Order not found" : "Could not load order details.");
    } finally {
      setLoading(false);
    }
  }, [order]);

  useEffect(() => { load(); }, [load]);

  // Catalog Fulfillment Scheduling (Sep 2026) — `null` for an ASAP
  // order, which then renders the single "ASAP" row instead.
  const schedulingPromise = fulfillmentPromise(orderDoc);
  const currency = (orderDoc?.currency || "USD").toUpperCase();
  // Which lines were taxed, from the order's own per-line record.
  const taxableByLine = useMemo(() => lineTaxableMap(orderDoc), [orderDoc]);
  const ps = (orderDoc?.payment_status || "pending").toLowerCase();
  const pm = (orderDoc?.payment_method || "").toLowerCase();
  // Kitchen/Delivery/Payment status-axis separation correction.
  const kitchenStatus = (orderDoc?.status || "placed").toLowerCase();
  const isDeliveryOrder = (orderDoc?.fulfillment?.mode || orderDoc?.fulfillment_mode) === "delivery";
  const deliveryStatus = orderDoc?.delivery_status || null;
  const isFullyRefunded = ps === "refunded";
  const isCardTouchedByStripe = pm === "card" && ["paid", "refunded", "partial_refund"].includes(ps);
  const canChangePaymentMethod = !isFullyRefunded && !isCardTouchedByStripe;

  const refunds = useMemo(() => payment?.refunds || [], [payment]);
  const alreadyRefundedCents = useMemo(
    () => refunds.reduce((n, r) => n + (Number(r.amount_cents) || 0), 0),
    [refunds],
  );

  // Tenant-Review Authorized-Amount Label Correction (Aug 2026,
  // UBE-ORD-000027) — confirmed defect: this drawer always labeled
  // `payment.amount_cents` "Amount captured", even for `ps === "authorized"`
  // (Stripe has only PUT A HOLD on the card — nothing captured yet).
  // `amount_cents` is the originally requested/authorized ceiling and
  // is NEVER overwritten by a smaller real capture (see
  // `payments/engine.py`'s own docstring) — it must never be shown as
  // "captured" unless a real capture actually happened.
  //   * authorized / capture_processing  → "Amount authorized", the
  //     authoritative capturable ceiling (`capturable_amount_cents`,
  //     refreshed by Stripe — falls back to `amount_cents` only for a
  //     legacy row that predates that field).
  //   * authorization_release_processing / authorization_released →
  //     "Authorization released" (never "Amount captured", never
  //     "Refunded" — no money ever moved for a released authorization).
  //   * every other status (paid, partial_refund, refunded, and every
  //     Local-Delivery "captured" intermediate status) → "Amount
  //     captured". For a genuine `capture_method="manual"` row,
  //     `captured_amount_cents` is the authoritative captured amount
  //     (correctly smaller than `amount_cents` for a partial capture);
  //     for an ordinary automatic-capture row that field is never
  //     populated (always 0), so this falls back to `amount_cents` —
  //     byte-identical to this drawer's behavior before this fix for
  //     every automatic-capture order.
  // Loyalty display correction (Sep 2026) — this drawer showed Total
  // $2.17 / Payment mode CASH / Paid and nothing else, so an operator
  // reading it could not tell that only $1.17 had ever been collected
  // and that only $1.17 could ever be refunded in money. The order total
  // stays exactly as it is; the allocation is shown beneath it.
  //
  // Derived from the order's OWN persisted loyalty snapshot through the
  // same helper the confirmation dialog and the receipts use, so the
  // figure staff were asked to collect, the figure the receipt prints
  // and the figure shown here can never disagree.
  const loyaltyBreakdown = useMemo(
    () => orderPaymentBreakdown(orderDoc), [orderDoc],
  );

  const paymentAmountRow = useMemo(() => {
    if (!payment) return null;
    const amountCents = payment.amount_cents;
    if (ps === "authorized" || ps === "capture_processing") {
      const cents = Number.isFinite(payment.capturable_amount_cents)
        ? payment.capturable_amount_cents : amountCents;
      return { label: "Amount authorized", cents };
    }
    if (ps === "authorization_release_processing" || ps === "authorization_released") {
      const cents = Number.isFinite(payment.released_amount_cents)
        ? payment.released_amount_cents : amountCents;
      return { label: "Authorization released", cents };
    }
    // Terminal payment-reliability correction (Sep 2026,
    // RUD-ORD-000004). Every branch below this point used to fall into
    // a single `return { label: "Amount captured", cents: amountCents }`
    // — so a PENDING payment that had collected nothing rendered
    // "Amount captured: $1.07", where $1.07 was `amount_cents`, the
    // amount REQUESTED. An operator reading that drawer could not tell
    // an abandoned attempt from a completed sale.
    //
    // This is the same defect the Aug 2026 UBE-ORD-000027 note above
    // records, one status class short: that round added `authorized`
    // and the two release states to the allowlist and left `pending`,
    // `failed`, `cancelled`, `superseded` and `reconciliation_required`
    // falling through to "captured".
    //
    // So the mapping is now exhaustive and explicit, and the DEFAULT is
    // the safe one. A status this file has never heard of reads as
    // "Amount requested" — never as money that was collected. Same
    // allowlist discipline as `retail/payment_evidence.py` on the
    // server, and for the same reason: over-claiming here is how staff
    // end up refunding an order nobody paid for.
    if (CAPTURED_PAYMENT_STATUSES.has(ps)) {
      // A genuine manual-capture row carries the authoritative captured
      // figure, correctly smaller than `amount_cents` for a partial
      // capture. An ordinary automatic-capture row never populates it,
      // so this falls back to `amount_cents` — byte-identical to this
      // drawer's long-standing behavior for every automatic-capture
      // order.
      if (payment.capture_method === "manual" && Number.isFinite(payment.captured_amount_cents)) {
        return { label: "Amount captured", cents: payment.captured_amount_cents };
      }
      return { label: "Amount captured", cents: amountCents };
    }
    if (ps === "partial_refund" || ps === "partially_refunded") {
      const captured = (payment.capture_method === "manual"
        && Number.isFinite(payment.captured_amount_cents))
        ? payment.captured_amount_cents : amountCents;
      return { label: "Amount captured", cents: captured };
    }
    if (ps === "refunded") {
      return { label: "Amount refunded", cents: alreadyRefundedCents || amountCents };
    }
    if (ps === "failed") {
      return { label: "Amount not collected", cents: amountCents };
    }
    if (ps === "cancelled" || ps === "canceled" || ps === "superseded") {
      return { label: "Payment cancelled", cents: amountCents };
    }
    if (ps === "reconciliation_required") {
      return { label: "Payment verification required", cents: amountCents };
    }
    // `pending`, `processing`, and anything unrecognised.
    return { label: "Amount requested", cents: amountCents };
  }, [payment, ps, alreadyRefundedCents]);

  // Which rail actually took (or attempted) this payment. Read from the
  // ledger row rather than inferred from the order, so a Terminal
  // attempt is never described as Hosted Checkout and vice versa — the
  // `cs_live_…`-vs-`pi_…` confusion on RUD-ORD-000004 is exactly what
  // this row exists to prevent.
  const paymentChannelLabel = useMemo(
    () => (payment ? paymentRailLabel(payment) : null), [payment]);

  // Server-authoritative refund headroom. Never derived here: the
  // backend nets confirmed collection against confirmed refunds (see
  // `services/recorded_refunds.remaining_refundable_cents`), and a
  // client-side estimate is exactly how a refund gets offered against
  // money that was never taken.
  // ONE answer, shared with the amount label above and with the orders
  // list, so the drawer can never print "Amount captured" beside "no
  // payment has been confirmed as collected" — nor hide the refund
  // control on a Local Delivery order whose money is genuinely
  // captured. The server remains the authority
  // (`services/recorded_refunds.py::_authoritative_capture`).
  const isRefundableState = isRefundablePaymentStatus(ps);

  const reconcileAmbiguousPayment = async () => {
    if (reconciling) return;
    if (!window.confirm(
      "Ask Stripe for this payment's true current state? This never retries the original " +
      "capture/release — it only reconciles our records to match what Stripe actually did."
    )) return;
    setReconciling(true);
    try {
      const { data } = await api.post(`/retail/orders/${order.id}/tenant-review/reconcile`);
      if (data.status === "still_ambiguous") {
        toast.error(data.message || "Stripe's true state still could not be confirmed. Try again shortly.");
      } else if (data.status === "captured_requires_tenant_review") {
        toast.error(data.message || "Payment was captured but requires your review.");
      } else {
        toast.success(data.message || "Reconciled.");
      }
      await load();
      onChanged && onChanged();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && typeof det === "object" && det.message) || "Could not reconcile this order.");
    } finally {
      setReconciling(false);
    }
  };

  const resumeFulfillment = async () => {
    if (resumingFulfillment) return;
    if (!window.confirm(
      "Resume fulfillment for this order? This re-confirms with Stripe that the payment was " +
      "captured, then completes Kitchen Accepted / courier dispatch — it never charges the " +
      "customer's card again."
    )) return;
    setResumingFulfillment(true);
    try {
      const { data } = await api.post(`/retail/orders/${order.id}/tenant-review/resume-fulfillment`);
      if (data.status === "still_ambiguous") {
        toast.error(data.message || "Stripe's true state still could not be confirmed. Try again shortly.");
      } else if (data.status === "captured_requires_tenant_review") {
        toast.error(data.message || "Payment was captured but requires your review.");
      } else if (data.status === "unstalled_to_authorized") {
        toast.success(data.message || "Nothing was captured — you may accept or reject this order again.");
      } else {
        toast.success(data.message || "Fulfillment resumed.");
      }
      await load();
      onChanged && onChanged();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && typeof det === "object" && det.message) || "Could not resume fulfillment for this order.");
    } finally {
      setResumingFulfillment(false);
    }
  };

  const submitPaymentMethod = async () => {
    const val = (newPm || "").trim().toLowerCase();
    if (!val) { toast.error("Choose a payment method."); return; }
    if (!pmReason.trim()) { toast.error("A reason is required for record corrections."); return; }
    setPmChanging(true);
    try {
      await api.patch(`/retail/orders/${order.id}/payment-method`, {
        payment_method: val,
        reason: pmReason.trim().slice(0, 280),
      });
      toast.success(`Payment method set to ${OPERATOR_PAYMENT_METHOD_OPTIONS.find(o => o.value === val)?.label || val}.`);
      await load();
      setNewPm("");
      setPmReason("");
      onChanged && onChanged();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = (det && typeof det === "object" && det.message)
        || (typeof det === "string" && det)
        || "Could not change payment method.";
      toast.error(msg);
    } finally {
      setPmChanging(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="order-details-drawer"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl h-full bg-white shadow-2xl border-l border-slate-200 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Order details</div>
            <h2 className="text-lg font-semibold tracking-tight" data-testid="order-details-code">
              {orderDoc?.order_code || order?.order_code}
            </h2>
            {/* Kitchen/Delivery/Payment status-axis separation
                correction (Aug 2026) — all three independent axes shown
                together, at a glance, so a completed kitchen status can
                never visually hide a delivery failure or vice versa. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span
                data-testid="order-details-kitchen-status"
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${KITCHEN_STATUS_COLOR[kitchenStatus] || KITCHEN_STATUS_COLOR.placed}`}
                title="Kitchen status"
              >
                {KITCHEN_STATUS_LABEL[kitchenStatus] || kitchenStatus}
              </span>
              {isDeliveryOrder && (
                <span
                  data-testid="order-details-delivery-status-summary"
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${deliveryStatus ? (DELIVERY_STATUS_COLOR[deliveryStatus] || "bg-slate-100 text-slate-600") : "bg-slate-100 text-slate-400"}`}
                  title="Delivery status"
                >
                  {deliveryStatus ? (DELIVERY_STATUS_LABEL[deliveryStatus] || deliveryStatus) : "—"}
                </span>
              )}
              <span
                data-testid="order-details-payment-status-summary"
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${paymentStatusColor(ps)}`}
                title="Payment status"
              >
                {paymentStatusLabel(ps, orderDoc?.payment_release_reason)}
              </span>
              {ps === "authorized" && (
                <button
                  type="button"
                  onClick={() => setShowTenantReview(true)}
                  data-testid="order-details-review-order-button"
                  className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-600 text-white hover:bg-amber-700"
                >
                  Review Order
                </button>
              )}
              {ps === "reconciliation_required" && orderDoc?.card_capture_mode === "tenant_review" && (
                <button
                  type="button"
                  onClick={reconcileAmbiguousPayment}
                  disabled={reconciling}
                  data-testid="order-details-reconcile-button"
                  className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
                >
                  {reconciling ? "Reconciling…" : "Reconcile with Stripe"}
                </button>
              )}
              {(ps === "paid_fulfillment_pending" || ps === "tenant_accepted") && orderDoc?.card_capture_mode === "tenant_review" && (
                <button
                  type="button"
                  onClick={resumeFulfillment}
                  disabled={resumingFulfillment}
                  data-testid="order-details-resume-fulfillment-button"
                  className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
                >
                  {resumingFulfillment ? "Resuming…" : "Payment Captured — Resume Fulfillment"}
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="order-details-close"
            className="text-slate-400 hover:text-slate-700 p-1 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {showTenantReview && (
          <TenantReviewModal
            orderId={orderDoc?.id || order?.id}
            onClose={() => setShowTenantReview(false)}
            onResolved={() => {
              setShowTenantReview(false);
              load();
              onChanged && onChanged();
            }}
          />
        )}

        {loading ? (
          <div className="py-20 grid place-items-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="m-5 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
            {error}
          </div>
        ) : (
          <div className="p-5 space-y-6 text-sm">
            {/* Customer + timing */}
            <section data-testid="order-details-header">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Customer" value={orderDoc?.customer?.name || "—"} />
                <Field label="Phone" value={orderDoc?.customer?.phone || "—"} />
                <Field label="Email" value={orderDoc?.customer?.email || "—"} />
                <Field label="Placed" value={orderDoc?.placed_at ? new Date(orderDoc.placed_at).toLocaleString() : "—"} />
                <Field label="Fulfillment mode" value={orderDoc?.fulfillment?.mode || orderDoc?.fulfillment_mode || "—"} />
                {orderDoc?.fulfillment?.table_number && (
                  <Field label="Table" value={orderDoc.fulfillment.table_number} />
                )}
              </div>
            </section>

            {/* Items + totals */}
            <section data-testid="order-details-items">
              <SectionTitle>Items</SectionTitle>
              <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-md">
                {(orderDoc?.items || []).map((it, i) => (
                  <li key={it.item_id || i} className="px-3 py-2 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-1.5">
                        {/* Product tax treatment (Oct 2026) — "(T)" marks a
                            line that was taxed, from the order's own
                            per-line record. */}
                        <span className="truncate" data-testid={`order-details-item-name-${it.item_id || i}`}>
                          {it.quantity}× {taxMarkedName(it.product_name, taxableByLine[it.item_id])}
                        </span>
                        {it.spice_level_label && (
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-rose-50 text-rose-800 border-rose-300 shrink-0"
                            data-testid={`order-details-spice-${it.item_id || i}`}
                          >
                            🌶 {it.spice_level_label}
                          </span>
                        )}
                        {/* Item-level Takeout marker (Sep 2026) — what the
                            customer asked to have packed. Informational
                            only: the order's own fulfillment mode, totals
                            and state are unaffected. */}
                        {it.item_takeout && (
                          <TakeoutBadge testId={`order-details-item-takeout-${it.item_id || i}`} />
                        )}
                      </div>
                      {it.note && <div className="text-xs text-slate-500 truncate">{it.note}</div>}
                    </div>
                    <div className="text-right text-slate-700 whitespace-nowrap">
                      <div>{formatMoney(it.line_total, currency)}</div>
                      <div className="text-[11px] text-slate-500">@ {formatMoney(it.unit_price, currency)}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-3 space-y-1">
                <Row label="Subtotal" value={formatMoney(orderDoc?.subtotal, currency)} />
                {Number(orderDoc?.discount) > 0 && (
                  <Row
                    label={orderDoc?.coupon?.coupon_code ? `Coupon ${orderDoc.coupon.coupon_code}` : "Discount"}
                    value={`− ${formatMoney(orderDoc?.discount, currency)}`}
                  />
                )}
                {Number(orderDoc?.order_discount) > 0 && (
                  <Row label="Order Discount" value={`− ${formatMoney(orderDoc?.order_discount, currency)}`} testid="order-details-order-discount" />
                )}
                <Row label="Tax" value={formatMoney(orderDoc?.tax, currency)} />
                {Object.values(taxableByLine).some(Boolean) && (
                  <div className="text-[11px] text-slate-500" data-testid="order-details-tax-legend">(T) = Taxable item</div>
                )}
                {Number(orderDoc?.delivery) > 0 && <Row label="Delivery" value={formatMoney(orderDoc?.delivery, currency)} />}
                {Number(orderDoc?.tip) > 0 && <Row label="Tip" value={formatMoney(orderDoc?.tip, currency)} />}
                <Row label="Total" value={formatMoney(orderDoc?.total, currency)} bold testid="order-details-total" />
              </div>
              {/* Order Discount (Sep 2026) — staff-applied manual discount
                  on an unpaid order. Server-authoritative; refreshes the
                  drawer and the orders list from the server afterwards. */}
              <OrderDiscountPanel
                order={orderDoc}
                currency={currency}
                onChanged={() => { load(); onChanged?.(); }}
              />
              {/* Card-reader recovery (Sep 2026) — cancel or check a
                  Stripe Terminal payment nobody finished. Renders nothing
                  unless the order has one staff can act on. */}
              <StripeTerminalRecoveryPanel
                order={orderDoc}
                onChanged={() => { load(); onChanged?.(); }}
              />
            </section>

            {/* CP2.5 — Parcel Shipping panel (read-only). Silently
                omitted for non-shipping orders (endpoint returns 404). */}
            {((orderDoc?.fulfillment?.mode || orderDoc?.fulfillment_mode) === "shipping") && (
              <OrderShippingPanel orderId={orderDoc?.id || order?.id} />
            )}

            {/* Phase 3A.5 — Local Delivery panel (read-only). Separate
                component/data path from Parcel Shipping above, per the
                engineering standards. Silently omitted for non-delivery
                orders (endpoint returns 404).
                Own Delivery/KDS status-wording correction (Aug 2026) —
                this panel renders Uber-specific courier wording
                ("Requesting courier…" etc.) that is actively false once
                an order has been converted to Own Delivery
                (`fulfillment_choice === "own"`); it was previously
                rendering ALONGSIDE `OwnDeliveryOperationalPanel` below
                for exactly those orders, since the two gates check
                different, independent fields and are not actually
                mutually exclusive. Excluded here so an Own Delivery
                order shows only its own, correct operational panel. */}
            {/* Gate C5A §7 — the plain-language Kitchen/Grocery card,
                ABOVE the operational panel. Deliberately first: the
                panel below is an operational console (statuses,
                identifiers, raw timestamps) and reads correctly to
                someone who already understands the pipeline. This card
                is for someone who does not, and during a rush it is the
                only thing they should have to read.

                Gated on exactly the same condition as the panel, so an
                Own Delivery order (which has no Uber quote lifecycle)
                never renders it. The component itself also renders
                nothing when the order has no delivery quote at all. */}
            {((orderDoc?.fulfillment?.mode || orderDoc?.fulfillment_mode) === "delivery")
              && orderDoc?.fulfillment_choice !== "own" && (
              <UberQuoteStaffCard
                orderId={orderDoc?.id || order?.id}
                onChanged={() => { load(); onChanged?.(); }}
              />
            )}

            {((orderDoc?.fulfillment?.mode || orderDoc?.fulfillment_mode) === "delivery")
              && orderDoc?.fulfillment_choice !== "own" && (
              <OrderLocalDeliveryPanel
                orderId={orderDoc?.id || order?.id}
                paymentStatus={orderDoc?.payment_status}
                onAccepted={() => { load(); onChanged?.(); }}
              />
            )}

            {/* Catalog Fulfillment PR 4 (Aug 2026) — Own Delivery
                operational panel. Gated on `fulfillment_choice === "own"`
                with a `local_delivery_id` present — the Uber-only panel
                above now explicitly excludes this same condition, so the
                two never both render for the same order. */}
            {orderDoc?.fulfillment_choice === "own" && orderDoc?.local_delivery_id && (
              <OwnDeliveryOperationalPanel orderId={orderDoc?.id || order?.id} />
            )}

            {/* Catalog Fulfillment PR 4 (Aug 2026) — Online Customer
                Pickup operational panel. Gated on the presence of
                `pickup_phase` (only ever set for a paid Online Customer
                Pickup order — the existing unpaid kiosk/walk-in Pickup
                workflow never sets this field, so it is untouched). */}
            {Boolean(orderDoc?.pickup_phase) && (
              <PickupOperationalPanel
                orderId={orderDoc?.id || order?.id}
                orderDoc={orderDoc}
                onUpdated={() => { load(); onChanged?.(); }}
              />
            )}

            {/* Payment block */}
            <section data-testid="order-details-payment">
              <SectionTitle>Payment</SectionTitle>
              <div className="mt-2 rounded-md border border-slate-200 p-3 bg-slate-50/60 space-y-1.5">
                <Row label="Payment mode" value={(pm || "—").toString().toUpperCase()} />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Payment status</span>
                  <span
                    data-testid="order-details-payment-status"
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${paymentStatusColor(ps)}`}
                  >
                    {paymentStatusLabel(ps, orderDoc?.payment_release_reason)}
                  </span>
                </div>
                {/* Loyalty allocation — rendered only for an order that
                    actually redeemed points. Every other order's Payment
                    block is unchanged. */}
                {loyaltyBreakdown?.hasLoyalty && (
                  <div
                    className="rounded border border-emerald-200 bg-emerald-50/70 px-2 py-2 space-y-1"
                    data-testid="order-details-loyalty-allocation"
                  >
                    <Row
                      label="Order total"
                      value={formatMoney(loyaltyBreakdown.totalCents / 100, currency)}
                    />
                    {loyaltyBreakdown.pointsRedeemed > 0 && (
                      <Row
                        label="Loyalty points redeemed"
                        value={String(loyaltyBreakdown.pointsRedeemed)}
                        testid="order-details-loyalty-points"
                      />
                    )}
                    <Row
                      label="Loyalty tender"
                      value={`− ${formatMoney(loyaltyBreakdown.loyaltyTenderCents / 100, currency)}`}
                      testid="order-details-loyalty-tender"
                    />
                    <Row
                      label={externalPaidLabel(pm).replace("paid", "collected")}
                      value={formatMoney(loyaltyBreakdown.externalCents / 100, currency)}
                      testid="order-details-external-collected"
                      bold
                    />
                    <Row
                      label="Refundable external amount"
                      value={formatMoney(loyaltyBreakdown.externalCents / 100, currency)}
                      testid="order-details-refundable-external"
                    />
                    <div className="text-[11px] text-emerald-900/80">
                      Points are not money and are never refundable as cash. Refunds on this
                      order are limited to the amount actually collected.
                    </div>
                  </div>
                )}
                {payment && paymentAmountRow && (
                  <>
                    <Row
                      label={paymentAmountRow.label}
                      value={formatMoney(centsToDollars(paymentAmountRow.cents), currency)}
                      testid="order-details-payment-amount"
                    />
                    {alreadyRefundedCents > 0 && (
                      <Row label="Already refunded" value={`− ${formatMoney(centsToDollars(alreadyRefundedCents), currency)}`} />
                    )}
                    {/* Which rail this actually was. Staff could not
                        previously tell a card-reader attempt from an
                        online Hosted Checkout one without decoding the
                        Stripe id by its prefix. */}
                    {paymentChannelLabel && (
                      <Row
                        label="Payment channel"
                        value={paymentChannelLabel}
                        testid="order-details-payment-channel"
                      />
                    )}
                    {/* Sep 2026 — this order was paid more than once.
                        One payment settled it; the rest is money to give
                        back. Never hidden to keep the single-payment
                        layout tidy: a double charge the staff cannot see
                        is a double charge nobody refunds. */}
                    {duplicateCollections.length > 0 && (
                      <div
                        className="text-[11px] text-red-800 bg-red-50 border border-red-300 rounded px-2 py-1 mt-1"
                        data-testid="order-details-duplicate-collection"
                      >
                        <div className="font-semibold">
                          This order was paid more than once — a refund is owed.
                        </div>
                        <ul className="mt-1 space-y-0.5">
                          {duplicateCollections.map((d) => (
                            <li key={d.id} data-testid={`order-details-duplicate-${d.id}`}>
                              {paymentRailLabel(d)} collected{" "}
                              <span className="font-semibold">
                                {formatMoney(centsToDollars(d.amount_cents || 0),
                                             d.currency || currency)}
                              </span>{" "}
                              in addition to the payment that settled this order.
                              Refund it from this charge, not by cancelling the order.
                              {/* Sep 2026 (third review) — invariant I-7.
                                  Naming the duplicate and then offering
                                  only the order's own Refund button was
                                  the trap: that button refunds the
                                  payment that PAID the order, so the
                                  double charge stayed and the customer's
                                  real payment went back. This control
                                  carries the duplicate's own payment
                                  id. */}
                              <DuplicateCollectionRefund
                                duplicate={d}
                                currency={currency}
                                railLabel={paymentRailLabel(d)}
                                formatMoney={formatMoney}
                                centsToDollars={centsToDollars}
                                onRefunded={onChanged}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* The canonical payment state, spelled out. An
                        order parked for verification must SAY so —
                        `reconciliation_required` means money may have
                        moved and nobody should be asked to tap again. */}
                    {ps === "reconciliation_required" && (
                      <div
                        className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1"
                        data-testid="order-details-reconciliation-notice"
                      >
                        Payment verification required — Stripe has not confirmed this
                        attempt. Do not ask the customer to pay again until it is resolved.
                      </div>
                    )}
                    {payment.reference_number && (
                      <Row label="Reference #" value={payment.reference_number} />
                    )}
                    {/* Sprint A — Stripe identifiers, redacted for safe display. */}
                    {payment.provider_session_id && (
                      <Row
                        label="Stripe session"
                        value={redactStripeId(payment.provider_session_id)}
                        mono
                        testid="order-details-stripe-session"
                      />
                    )}
                    {payment.provider_payment_intent_id && (
                      <Row
                        label="Stripe intent"
                        value={redactStripeId(payment.provider_payment_intent_id)}
                        mono
                        testid="order-details-stripe-intent"
                      />
                    )}
                  </>
                )}
              </div>

              {/* Refund CTA */}
              {["paid", "partial_refund"].includes(ps) && payment?.id && !isFullyRefunded && (
                <button
                  type="button"
                  onClick={onRequestRefund}
                  data-testid="order-details-refund-btn"
                  className="mt-3 inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-slate-300 hover:border-orange-400 hover:bg-orange-50"
                >
                  Issue refund
                </button>
              )}
              {isFullyRefunded && (
                <div className="mt-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 inline-flex items-center gap-2" data-testid="order-details-locked-banner">
                  <Info className="w-3.5 h-3.5" />
                  This order is <strong>Fully Refunded</strong> and is locked against further status or refund changes.
                </div>
              )}
            </section>

            {/* Refund history */}
            {refunds.length > 0 && (
              <section data-testid="order-details-refund-history">
                <SectionTitle>Refund history</SectionTitle>
                <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-md">
                  {refunds.map((r, i) => (
                    <li key={r.id || i} className="px-3 py-2 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{formatMoney(centsToDollars(r.amount_cents), currency)}</div>
                        <div className="text-xs text-slate-500 truncate">{r.reason || "—"}</div>
                      </div>
                      <div className="text-[11px] text-slate-400 whitespace-nowrap">
                        {r.refunded_at ? new Date(r.refunded_at).toLocaleString() : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Loyalty Phase 1 (Sep 2026) — refunds completed OUTSIDE
                Rudras-AI: cash handed back at the counter, and a Clover
                refund the owner already performed in Clover itself.
                This is the only remedy for a Clover refund, because
                `CloverTerminalProvider.refund` reports `unsupported` and
                the engine now refuses to fabricate one. Its own panel so
                the wording, the owner gate and the durable request id
                live in one place. */}
            {order?.id && (
              <RecordedRefundPanel orderId={order.id} refundable={isRefundableState} />
            )}

            {/* Operator: change payment method */}
            <section data-testid="order-details-change-payment">
              <SectionTitle>Change payment method (operator)</SectionTitle>
              <p className="text-[11px] text-slate-500 mt-1">
                Record-correction only. No accounts-receivable automation. Card orders captured via Stripe cannot be re-classified.
              </p>
              {canChangePaymentMethod ? (
                <div className="mt-2 space-y-2">
                  <select
                    value={newPm}
                    onChange={(e) => setNewPm(e.target.value)}
                    disabled={pmChanging}
                    data-testid="order-details-pm-select"
                    className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm bg-white"
                  >
                    <option value="">Select new payment method…</option>
                    {OPERATOR_PAYMENT_METHOD_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <textarea
                    value={pmReason}
                    onChange={(e) => setPmReason(e.target.value)}
                    disabled={pmChanging}
                    data-testid="order-details-pm-reason"
                    className="w-full min-h-[52px] rounded-md border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Reason for record correction (required)"
                    maxLength={280}
                  />
                  <button
                    type="button"
                    onClick={submitPaymentMethod}
                    disabled={pmChanging || !newPm || !pmReason.trim()}
                    data-testid="order-details-pm-submit"
                    className="w-full h-10 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:opacity-50"
                  >
                    {pmChanging ? "Saving…" : "Save payment method"}
                  </button>
                </div>
              ) : (
                <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 text-slate-600 text-xs px-3 py-2" data-testid="order-details-pm-locked">
                  {isCardTouchedByStripe
                    ? "Card orders processed through Stripe cannot be re-classified. Contact support for a manual correction."
                    : "This order is fully refunded and cannot be re-classified."}
                </div>
              )}

              {/* Change history */}
              {(orderDoc?.payment_method_history || []).length > 0 && (
                <ul className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded-md text-xs" data-testid="order-details-pm-history">
                  {(orderDoc.payment_method_history || []).map((h, i) => (
                    <li key={i} className="px-3 py-2 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div><strong>{(h.from || "—").toUpperCase()}</strong> → <strong>{(h.to || "").toUpperCase()}</strong></div>
                        <div className="text-slate-500 truncate">by {h.operator_email || h.operator_id || "—"} · {h.reason || "—"}</div>
                      </div>
                      <div className="text-[10px] text-slate-400 whitespace-nowrap">
                        {h.at ? new Date(h.at).toLocaleString() : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Order status history */}
            {(orderDoc?.status_history || []).length > 0 && (
              <section data-testid="order-details-status-history">
                <SectionTitle>Status history</SectionTitle>
                <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-md text-xs">
                  {orderDoc.status_history.map((h, i) => (
                    <li key={i} className="px-3 py-2 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div><strong>{(h.from || "—")}</strong> → <strong>{(h.to || "")}</strong></div>
                        <div className="text-slate-500 truncate">by {h.operator_email || h.operator_id || "—"}</div>
                      </div>
                      <div className="text-[10px] text-slate-400 whitespace-nowrap">
                        {h.at ? new Date(h.at).toLocaleString() : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{children}</div>;
}
function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900">{value}</div>
    </div>
  );
}
function Row({ label, value, bold = false, mono = false, testid }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span
        data-testid={testid}
        className={`${bold ? "font-bold text-slate-900" : "text-slate-800"} ${mono ? "font-mono text-xs" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}
